import { esc, header, footer, openPrintDoc } from './printShared';

/**
 * High School Fundraiser Night — the full event plan, as a branded sheet the
 * owner prints and hands to the coach.
 *
 * Unlike the other Print Kit assets this one is not assembled from playbook
 * fragments: it is a standalone, self-contained event template (run of show,
 * games, sponsor levels, raffle, revenue model), so the content lives here. The
 * blanks — host location, date, contact — are deliberately left as rules to
 * write on, because the coach fills them in with the owner.
 */

interface Row {
  a: string;
  b: string;
}

const SCHEDULE: Row[] = [
  { a: '6:00 – 6:15 PM', b: 'Check-in, silent auction opens, team meet & greet' },
  { a: '6:15 – 6:25 PM', b: 'Welcome & coach introduction' },
  { a: '6:25 – 6:40 PM', b: 'Player introductions' },
  { a: '6:40 – 7:45 PM', b: 'Fundraiser competitions open' },
  { a: '7:45 – 7:55 PM', b: 'Awards & raffle drawings' },
  { a: '7:55 – 8:00 PM', b: 'Thank you & donation opportunity' },
];

const SPONSOR_LEVELS: Row[] = [
  { a: 'Bronze', b: '$100' },
  { a: 'Silver', b: '$250' },
  { a: 'Gold', b: '$500' },
  { a: 'Eagle', b: '$1,000' },
];

const RAFFLE_PRICING: Row[] = [
  { a: '1 ticket', b: '$5' },
  { a: '5 tickets', b: '$20' },
  { a: '15 tickets', b: '$50' },
];

const REVENUE: Row[] = [
  { a: 'Closest to the Hole', b: '$400' },
  { a: 'PuttView Challenge', b: '$400' },
  { a: 'Beat the Player', b: '$300' },
  { a: 'Silent auction', b: '$1,000' },
  { a: 'Sponsorships', b: '$2,000' },
  { a: 'Donations', b: '$700' },
  { a: 'Raffle', b: '$700' },
];
const REVENUE_TOTAL = '$5,500';

const WELCOME = [
  'Introduction from the head coach',
  'Team goals for the season',
  'Recognition of sponsors',
  'Thank parents and community supporters',
  'Explain the fundraising purpose',
];

const MEET_THE_TEAM = [
  'Name',
  'Grade',
  'Favorite course',
  'Favorite PGA/LPGA player',
  'College goals (optional)',
  'Fun golf fact',
];

const GAMES: { name: string; price: string; lines: string[] }[] = [
  {
    name: 'Closest to the Hole Challenge',
    price: '$5 per shot · 3 for $10',
    lines: [
      'Pick a fun hole — Black Desert #17 or Pebble Beach #7',
      'Run separate men’s and women’s divisions',
    ],
  },
  {
    name: 'PuttView Putting Derby',
    price: '$15 entry',
    lines: [
      '12-foot breaking, downhill putt',
      'Elimination format until one player remains',
    ],
  },
  {
    name: 'Beat a Varsity Player',
    price: '$25 entry',
    lines: [
      'Challenge one varsity golfer — e.g. Hillcrest Five, par 3, auto putt',
      'Guest wins: prize (balls, towel). Player wins: the team keeps the donation',
    ],
  },
];

const PRIZES = [
  'Golf balls',
  'Gift cards from local companies and restaurants',
  'A free lesson from the Box Pro',
  'Grand prize — putter, wedge, etc.',
];

const AUCTION_ITEMS = [
  'Foursome golf rounds (the team gets local courses involved)',
  'Lessons — the Box Pro donates one or two',
  'Driver fitting',
  'Signed memorabilia',
  'Restaurant gift cards',
  'Local business baskets',
  'Golf bags',
  'Rangefinders',
];

const SPONSOR_BENEFITS = [
  'Team website recognition',
  'Social media recognition',
  'Banner at home events',
  'Tee Box recognition — your opening to sell a bay sponsorship',
];

const MERCH = [
  'Team hats',
  'Shirts',
  'Hoodies',
  'Ball markers',
  'Towels',
  'Raffle tickets',
];

const RAFFLE_PRIZES = [
  'Driver fitting',
  'A free month at Tee Box',
  'Golf balls',
  'Putter',
  'Golf bag',
  'Local restaurant gift cards',
  'Team merchandise',
];

const AWARDS = [
  'Closest to the Hole',
  'PuttView champion',
  'Long drive',
  'Beat the Player champion',
  'Raffle winners',
];

const TIPS = [
  'Assign each varsity player to a station — they explain the challenge and thank every participant. The more interaction, the better.',
  'Get parents involved. They can run food, drinks, advertising and prizes.',
  'Set up a donation station with QR codes for quick mobile giving.',
  'Capture photos and short video all night to share and to thank sponsors afterwards — do it with the school’s golf social account.',
];

const ul = (items: string[], box = false) =>
  `<ul>${items
    .map((i) => `<li>${box ? '<span class="box"></span>' : '<span class="dot"></span>'}${esc(i)}</li>`)
    .join('')}</ul>`;

// `alignRight` is for money/number columns only — prose columns (the schedule's
// Activity) stay left-aligned or the table reads as two disconnected lists.
const table = (
  headA: string,
  headB: string,
  rows: Row[],
  opts: { alignRight?: boolean; total?: Row } = {},
) => {
  const c = opts.alignRight ? ' class="r"' : '';
  return `<table><thead><tr><th>${esc(headA)}</th><th${c}>${esc(
    headB,
  )}</th></tr></thead><tbody>${rows
    .map((r) => `<tr><td>${esc(r.a)}</td><td${c}>${esc(r.b)}</td></tr>`)
    .join('')}${
    opts.total
      ? `<tr class="tot"><td>${esc(opts.total.a)}</td><td${c}>${esc(
          opts.total.b,
        )}</td></tr>`
      : ''
  }</tbody></table>`;
};

export function printFundraiserNight(): void {
  const fillIn = `<div class="card meta">
    <div class="m"><span class="mk">Host</span><span class="fill">Tee Box</span></div>
    <div class="m"><span class="mk">Date &amp; time</span><span class="fill"></span></div>
    <div class="m"><span class="mk">School / team</span><span class="fill"></span></div>
    <div class="m"><span class="mk">Coach contact</span><span class="fill"></span></div>
    <div class="m"><span class="mk">Duration</span><span class="val">2 hours</span></div>
    <p class="purpose">Introduce the high school golf team to the community while raising
    funds for the season through games, competitions, sponsorships and donations.</p>
  </div>`;

  const games = GAMES.map(
    (g) =>
      `<div class="card game"><div class="gh"><span class="gn">${esc(
        g.name,
      )}</span><span class="gp">${esc(g.price)}</span></div>${ul(g.lines)}</div>`,
  ).join('');

  const css = `
    .meta { margin-top:16px; }
    .m { display:flex; align-items:baseline; gap:12px; padding:5px 0; font-size:12.5px; }
    .mk { min-width:120px; font-size:10px; letter-spacing:.12em; text-transform:uppercase; color:#B58F2D; font-weight:700; }
    .fill { flex:1; border-bottom:1px solid #E6DFCF; min-height:17px; color:#16241E; }
    .val { color:#16241E; }
    .purpose { margin:10px 0 0; font-size:12.5px; line-height:1.55; color:#16241E; font-style:italic; }
    table { width:100%; border-collapse:collapse; margin-top:4px; }
    thead th { background:#0C3D2D; color:#FBF6EC; font-size:11px; font-weight:700; text-align:left; padding:7px 10px; }
    tbody td { border-bottom:1px solid #E6DFCF; font-size:12.5px; padding:6px 10px; color:#16241E; }
    .r { text-align:right; }
    tr.tot td { background:#F1E8D6; font-weight:700; color:#0C3D2D; border-bottom:none; }
    ul { list-style:none; margin:4px 0 0; padding:0; }
    li { display:flex; gap:9px; font-size:12.5px; line-height:1.5; padding:3px 0; color:#16241E; }
    .dot { flex:none; width:6px; height:6px; border-radius:50%; background:#B58F2D; margin-top:7px; }
    .box { margin-top:1px; }
    .two { display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-top:4px; align-items:start; }
    .game { margin-top:10px; }
    .gh { display:flex; justify-content:space-between; align-items:baseline; gap:10px; }
    .gn { font-size:14px; font-weight:700; color:#0C3D2D; }
    .gp { font-size:11.5px; font-weight:700; color:#7C6220; background:rgba(181,143,45,.12); padding:3px 9px; border-radius:999px; white-space:nowrap; }
    .blk { margin-top:10px; }
    .blk h3 { margin:0 0 4px; font-size:13px; font-weight:700; color:#0C3D2D; }
    .note { margin-top:14px; border-left:3px solid #B58F2D; background:#F1E8D6; border-radius:8px; padding:12px 14px; font-size:12.5px; line-height:1.55; font-style:italic; color:#16241E; }
    .pgb { break-before:page; page-break-before:always; }`;

  const body =
    header(
      'High School Golf',
      '“Meet the Team Night” Fundraiser',
      'A 2-hour community night that introduces the team, fills the Box with their families, and raises the season’s funds.',
    ) +
    fillIn +
    '<h2>Event Schedule</h2>' +
    table('Time', 'Activity', SCHEDULE) +
    `<div class="two">
      <div class="card blk"><h3>Welcome · 6:15 PM</h3>${ul(WELCOME)}</div>
      <div class="card blk"><h3>Meet the Team · 6:25 PM</h3><p style="margin:0;font-size:12.5px;color:#6C756D;">Each player introduces:</p>${ul(
        MEET_THE_TEAM,
      )}<p style="margin:6px 0 0;font-size:11.5px;font-style:italic;color:#7C6220;">Optional: player walk-up music.</p></div>
    </div>` +
    '<h2>Fundraiser Competitions · 6:40 – 7:45 PM</h2>' +
    games +
    `<div class="card blk"><h3>Prizes for every game</h3>${ul(PRIZES)}</div>` +
    '<div class="pgb"></div>' +
    '<h2>Silent Auction</h2>' +
    `<div class="card blk"><p style="margin:0 0 4px;font-size:12.5px;font-style:italic;color:#6C756D;">The team sources the items. Suggested:</p>${ul(
      AUCTION_ITEMS,
      true,
    )}</div>` +
    '<h2>Team Sponsorship Wall</h2>' +
    `<div class="two">
      <div>${table('Level', 'Donation', SPONSOR_LEVELS, { alignRight: true })}</div>
      <div class="card blk"><h3>Sponsors receive</h3>${ul(SPONSOR_BENEFITS)}</div>
    </div>` +
    '<h2>Team Merchandise &amp; Raffle</h2>' +
    `<div class="two">
      <div class="card blk"><h3>Merchandise the team can sell</h3>${ul(MERCH)}</div>
      <div class="card blk"><h3>Raffle prizes</h3>${ul(RAFFLE_PRIZES)}</div>
    </div>` +
    '<h2>Raffle Ticket Pricing</h2>' +
    table('Package', 'Price', RAFFLE_PRICING, { alignRight: true }) +
    '<h2>Awards · 7:45 PM</h2>' +
    `<div class="card blk">${ul(AWARDS, true)}</div>` +
    '<h2>Estimated Revenue</h2>' +
    table('Activity', 'Estimated revenue', REVENUE, {
      alignRight: true,
      total: { a: 'Estimated total', b: REVENUE_TOTAL },
    }) +
    '<h2>Tips for a Successful Event</h2>' +
    `<div class="card blk">${ul(TIPS)}</div>` +
    `<p class="note">This format combines a fun community event with real interaction between
      the team and its supporters, while creating multiple fundraising opportunities across
      the evening. Capture every guest's contact details on the way in — that list is the
      point.</p>` +
    footer('Run it with the coach. Keep the list.');

  openPrintDoc('Tee Box — High School Fundraiser Night', css, body);
}
